/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import { isRightToLeft } from 'js/lib/language';

import { Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';
import { ArrowNextIcon, YellowStarIcon } from '@coursera/cds-icons';

import type { Asset } from 'bundles/ent-website/__generated__/baseContentfulTypes';
import ContentfulLink from 'bundles/ent-website/contentful/core/ContentfulLink';
import Image from 'bundles/ent-website/contentful/core/Image';
import type { BlockLevelModule } from 'bundles/ent-website/contentful/core/Module';

import _t from 'i18n!nls/ent-website';

const styles: Record<string, Interpolation<Theme>> = {
  cardContainer: () => css`
    padding: 0 var(--cds-spacing-50);
  `,

  card: () => css`
    border-radius: 8px;
    background: var(--cds-color-white-0);
    color: var(--cds-color-neutral-primary);
    height: 220px;
    overflow: hidden;
    margin: 8px 16px 8px 0;
    width: 100%;

    ${breakpoints.up('md')} {
      width: 275px;
      height: 380px;
    }

    display: block;
    cursor: pointer;
    transition: all 0.5s;

    :hover,
    :link,
    :active,
    :visited {
      text-decoration: none;
    }

    @media (hover: hover) and (pointer: fine) {
      :hover {
        box-shadow: rgb(99 99 99 / 20%) 0 2px 8px 0;
        transform: translateY(-4px);
      }
    }
  `,
  cardImageContainer: () => css`
    width: 100%;
    height: 0;
    overflow: hidden;

    ${breakpoints.up('md')} {
      height: 180px;
    }
  `,
  cardImage: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
  `,
  cardContentContainer: css`
    width: 100%;
    height: 200px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `,
  academyTitle: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
  academyDescription: () => css`
    color: var(--cds-color-grey-600);
    margin-bottom: var(--cds-spacing-200);
    font-size: 14px;
  `,
  reviewsContainer: css`
    display: flex;
    font-size: 12px;
    font-weight: 700;
  `,
  reviews: () => css`
    color: var(--cds-color-grey-600);
    font-weight: 400;
  `,
  // Styling the star is a bit tricky because want a solid star without a border.
  // I've made the border white to match the card, and added offsets to make it appear
  // the correct size
  star: css`
    margin-right: 1px;
    margin-top: 1px;
    margin-left: -3px;
    stroke: var(--cds-color-white-0);
  `,
  cartCta: css`
    vertical-align: middle;
    margin-right: 0.25em;
  `,
  cartCtaSvg: css`
    vertical-align: middle;
    display: inline-block;
    margin-top: 1px;
  `,
  academyLearnMore: () => css`
    a,
    a:visited {
      text-decoration: none;
      color: var(--cds-color-blue-700);
    }

    a:hover {
      text-decoration: underline;
    }
  `,
};

type Props = {
  image: Pick<Asset, 'url'> & Pick<Asset, 'title'>;
  academyTitle: string;
  description: string;
  starRating: number;
  reviewCount: string;
  academyUrl: string;
  module: BlockLevelModule;
  index: number;
};

function AcademyCard({ image, academyTitle, description, starRating, reviewCount, academyUrl, module, index }: Props) {
  const shouldUseRTL = isRightToLeft(_t.getLocale());
  return (
    <div css={styles.cardContainer} dir={shouldUseRTL ? 'rtl' : undefined}>
      <a css={styles.card} href={academyUrl} target="_blank" rel="noreferrer" data-testid="AcademyCard_anchor">
        <div css={styles.cardImageContainer}>
          <Image content={image} width="auto" css={styles.cardImage} />
        </div>
        <div css={styles.cardContentContainer}>
          <div>
            <Typography2 component="h3" css={styles.academyTitle} variant="subtitleMedium">
              {academyTitle}
            </Typography2>
            <Typography2 component="p" css={styles.academyDescription} variant="bodySecondary">
              {description}
            </Typography2>

            <div>
              <Typography2 component="p" css={styles.reviewsContainer} variant="bodyPrimary">
                <YellowStarIcon css={styles.star} size="medium" />
                {starRating}&nbsp;<span css={styles.reviews}>({reviewCount})</span>
              </Typography2>
            </div>
          </div>

          <Typography2 css={styles.academyLearnMore} variant="subtitleMedium" component="p">
            <ContentfulLink
              module={module}
              href={academyUrl}
              text={_t('Learn more')}
              position={index}
              typographyColor="body"
              css={styles.cartCta}
              icon={<ArrowNextIcon css={styles.cartCtaSvg} size="small" />}
            />
          </Typography2>
        </div>
      </a>
    </div>
  );
}

export default AcademyCard;
